'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useRouter } from '@/app/navigation/routerProgress';
import { toast } from '@/app/utils/toast';
import { useAuth } from '@/app/contexts/AuthContext';
import { acknowledgeNotification, getNotifications } from './notificationApi';
import { createNotificationDelivery } from './notificationDelivery';
import { notificationHref, type PortalNotification } from './notificationHref';
import { isDiscountDecision, showDiscountPopup } from './discountPresentation';
import { isCreditDecision, showCreditPopup } from './creditPresentation';
import { documentNotificationDisplay, isDocumentUpload, showDocumentPopup } from './documentPresentation';
import { isPaymentUpdate, showPaymentPopup } from './paymentPresentation';
import { signaturePresentation } from './signaturePresentation';
import { downloadFundingFile, fundingError, viewFundingFile } from '@/app/actions/motorFunding';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { acknowledgeCachedNotification, addCachedNotification, clearNotificationSession, readNotificationCache, viewCachedNotification, writeNotificationCache, type CachedNotification } from './notificationSessionCache';
import { allowsPortalNotifications } from './notificationRoute';

const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/$/, '');

type NotificationContextValue = {
  notifications: CachedNotification[];
  unreadCount: number;
  openNotification: (notification: CachedNotification) => void;
  markAllViewed: () => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

/** Read the session notification history and local view actions from the root provider. */
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
}

/**
 * Own the single authenticated Echo subscription and notification session cache.
 * This provider is mounted by the root layout so page navigation never creates
 * another subscription. Its cleanup disconnects Echo and dismisses active toasts.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, token, userId } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const notificationsAllowed = allowsPortalNotifications(pathname);
  const [notifications, setNotifications] = useState<CachedNotification[]>([]);
  const [decisionPopups, setDecisionPopups] = useState<PortalNotification[]>([]);
  const notificationsRef = useRef<CachedNotification[]>([]);
  const lastIdentity = useRef<{ userId: number; token: string } | null>(null);
  const deliveryRef = useRef<ReturnType<typeof createNotificationDelivery> | null>(null);

  // Keep the per-user cache across reloads, but clear it on logout or identity change.
  useEffect(() => {
    const current = isAuthenticated && token && userId ? { userId, token } : null;
    const previous = lastIdentity.current;
    if (previous && (!current || current.userId !== previous.userId || current.token !== previous.token)) {
      setDecisionPopups([]);
      clearNotificationSession(window.sessionStorage, previous.userId);
    }
    if (current && (!previous || current.userId !== previous.userId || current.token !== previous.token)) {
      notificationsRef.current = readNotificationCache(window.sessionStorage, current.userId);
      setNotifications(notificationsRef.current);
    } else if (!current) {
      notificationsRef.current = [];
      setNotifications([]);
    }
    lastIdentity.current = current;
  }, [isAuthenticated, token, userId]);

  const addNotification = useCallback((notification: PortalNotification) => {
    if (!userId) return;
    const next = addCachedNotification(notificationsRef.current, notification);
    notificationsRef.current = next;
    setNotifications(next);
    writeNotificationCache(window.sessionStorage, userId, next);
  }, [userId]);

  const markAcknowledged = useCallback((id: string) => {
    if (!userId) return;
    const next = acknowledgeCachedNotification(notificationsRef.current, id);
    notificationsRef.current = next;
    setNotifications(next);
    writeNotificationCache(window.sessionStorage, userId, next);
  }, [userId]);

  /** A user view updates the badge immediately and starts the backend DELETE. */
  const markViewed = useCallback((notification: CachedNotification) => {
    if (!userId) return;
    const next = viewCachedNotification(notificationsRef.current, notification.id);
    notificationsRef.current = next;
    setNotifications(next);
    writeNotificationCache(window.sessionStorage, userId, next);
    void deliveryRef.current?.acknowledgeViewed(notification);
  }, [userId]);

  const openNotification = (notification: CachedNotification) => {
    markViewed(notification);
    router.push(notificationHref(notification));
    if (isDiscountDecision(notification)) window.dispatchEvent(new CustomEvent('portal-discount-open', { detail: notification }));
    if (isCreditDecision(notification) || isDocumentUpload(notification) || isPaymentUpdate(notification)) window.dispatchEvent(new CustomEvent('portal-notification', { detail: notification }));
  };

  const markAllViewed = () => {
    if (!userId) return;
    const current = notificationsRef.current;
    const now = new Date().toISOString();
    const next = current.map(item => ({ ...item, viewed_at: item.viewed_at ?? now }));
    notificationsRef.current = next;
    setNotifications(next);
    writeNotificationCache(window.sessionStorage, userId, next);
    for (const item of current) if (!item.acknowledged_at) void deliveryRef.current?.acknowledgeViewed(item);
  };

  // Authentication is stored in localStorage and therefore shared by tabs.
  // Never let that shared session render portal popups over login or public forms.
  useEffect(() => {
    if (!notificationsAllowed) setDecisionPopups([]);
  }, [notificationsAllowed]);

  // Connect once per token/user. Recovery runs on startup, online, and Pusher connect.
  useEffect(() => {
    if (!notificationsAllowed || !isAuthenticated || !token || !userId || !apiBase) return;

    const toastIds = new Set<string>();
    const cachedNotifications = readNotificationCache(window.sessionStorage, userId);
    let disposed = false;
    const delivery = createNotificationDelivery({
      userId,
      storage: window.sessionStorage,
      alreadyShown: cachedNotifications.map(item => item.id),
      alreadyAcknowledged: cachedNotifications.filter(item => item.acknowledged_at).map(item => item.id),
      list: () => getNotifications(token),
      acknowledge: id => acknowledgeNotification(id, token),
      onAcknowledged: markAcknowledged,
      present: (notification, source) => {
        const signatureMode = signaturePresentation(notification, source, window.location.pathname, window.location.search);
        if (signatureMode === 'popup' || showDiscountPopup(notification, source, window.location.pathname, window.location.search) || showCreditPopup(notification, source, window.location.pathname, window.location.search) || showDocumentPopup(notification, source, window.location.pathname, window.location.search) || showPaymentPopup(notification, source, window.location.pathname, window.location.search)) {
          setDecisionPopups(items => [...items, notification]);
          if (isPaymentUpdate(notification)) addNotification(notification);
          window.dispatchEvent(new CustomEvent('portal-notification', { detail: notification }));
          if (notification.type === 'motor_further_discount_accepted') {
            router.replace(notificationHref(notification));
            window.dispatchEvent(new CustomEvent('portal-discount-open', { detail: notification }));
          }
          return true;
        }
        if (signatureMode === 'inbox' || isDiscountDecision(notification) || isCreditDecision(notification) || isDocumentUpload(notification) || isPaymentUpdate(notification)) {
          addNotification(notification);
          // A recovered capture may already have been rejected. Initial page loading
          // checks current approval status instead of replaying an old capture.
          if (signatureMode !== 'inbox') window.dispatchEvent(new CustomEvent('portal-notification', { detail: notification }));
          return true;
        }
        const toastId = `portal-notification:${userId}:${notification.id}`;
        toastIds.add(toastId);
        toast(
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">{documentNotificationDisplay(notification).title}</p>
            <p className="mt-1 text-sm text-slate-600">{documentNotificationDisplay(notification).message}</p>
            <button
              type="button"
              className="mt-3 rounded-md bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
              onClick={() => {
                if (disposed) return;
                markViewed(notification);
                router.push(notificationHref(notification));
                toast.dismiss(toastId);
              }}
            >
              Open
            </button>
          </div>,
          {
            toastId,
            autoClose: source === 'live' ? 10000 : 12000,
            closeOnClick: false,
          },
        );
        // Keep live and recovered notifications in the bell as soon as the toast is accepted.
        addNotification(notification);
        window.dispatchEvent(new CustomEvent('portal-notification', { detail: notification }));
        // The toast has now been delivered. Remove its backend fallback so it
        // is not recovered and shown again after logout and a later login.
        return 'acknowledge';
      },
    });
    deliveryRef.current = delivery;

    // Upgrade items displayed under the previous behavior: ordinary toast
    // entries already in this tab's cache can be acknowledged without replay.
    // Interactive inbox/dialog notifications still wait for the user.
    for (const notification of cachedNotifications) {
      const interactive = signaturePresentation(notification, 'recovered', window.location.pathname, window.location.search) === 'inbox'
        || isDiscountDecision(notification)
        || isCreditDecision(notification)
        || isDocumentUpload(notification)
        || isPaymentUpdate(notification);
      if (!interactive && !notification.acknowledged_at) void delivery.acknowledgeViewed(notification);
    }

    void delivery.recover().catch(error => console.error('Failed to recover notifications:', error));
    const recover = () => {
      void delivery.recover().catch(error => console.error('Failed to recover notifications:', error));
    };
    window.addEventListener('online', recover);
    const retryTimer = window.setInterval(delivery.retryPending, 60000);
    let disconnect: (() => void) | undefined;

    if (process.env.NEXT_PUBLIC_PUSHER_APP_KEY && process.env.NEXT_PUBLIC_PUSHER_APP_CLUSTER) {
      void Promise.all([import('laravel-echo'), import('pusher-js')]).then(([{ default: Echo }, { default: Pusher }]) => {
        if (disposed) return;
        const echo = new Echo({
          broadcaster: 'pusher',
          key: process.env.NEXT_PUBLIC_PUSHER_APP_KEY!,
          cluster: process.env.NEXT_PUBLIC_PUSHER_APP_CLUSTER!,
          forceTLS: true,
          authEndpoint: `${apiBase}/api/broadcasting/auth`,
          auth: { headers: { Authorization: `Bearer ${token}` } },
          Pusher,
        });
        const channelName = `App.Models.User.${userId}`;
        echo.private(channelName).notification((notification: PortalNotification) => {
          delivery.showNotification(notification, 'live');
        });
        echo.connector.pusher.connection.bind('connected', recover);
        disconnect = () => { echo.leave(channelName); echo.disconnect(); };
      }).catch(error => console.error('Notification connection failed:', error));
    }

    return () => {
      disposed = true;
      delivery.dispose();
      if (deliveryRef.current === delivery) deliveryRef.current = null;
      window.removeEventListener('online', recover);
      window.clearInterval(retryTimer);
      disconnect?.();
      for (const id of Array.from(toastIds)) toast.dismiss(id);
    };
  }, [notificationsAllowed, isAuthenticated, token, userId, router, addNotification, markViewed, markAcknowledged]);

  const popup = decisionPopups[0];
  const closeDecision = () => {
    if (!popup) return;
    if (isPaymentUpdate(popup)) markViewed(popup);
    else void deliveryRef.current?.acknowledgeViewed(popup);
    setDecisionPopups(items => items.slice(1));
  };

  return <NotificationContext.Provider value={{ notifications, unreadCount: notifications.filter(item => !item.viewed_at).length, openNotification, markAllViewed }}>
    {children}
    <Dialog open={!!popup} onOpenChange={open => { if (!open) closeDecision(); }}>
      <DialogContent hideClose onPointerDownOutside={event => event.preventDefault()} onEscapeKeyDown={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{popup && documentNotificationDisplay(popup).title}</DialogTitle>
          <DialogDescription>{popup && documentNotificationDisplay(popup).message}</DialogDescription>
        </DialogHeader>
        {popup && !isDocumentUpload(popup) && !isPaymentUpdate(popup) && <p className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-sm">{popup.metadata?.response_comment?.trim() || 'No message provided by the approver.'}</p>}
        <DialogFooter>
          {popup && popup.type.endsWith('_submitted') && popup.metadata?.submission_id && <Button variant="outline" onClick={() => void viewFundingFile(`/motor-funding/submissions/${popup.metadata.submission_id}/view`).catch(error => toast.error(fundingError(error)))}>View</Button>}
          {popup?.metadata?.receipt_id && <>
            <Button variant="outline" onClick={() => void viewFundingFile(`/motor-funding/receipts/${popup.metadata.receipt_id}/view`).catch(error => toast.error(fundingError(error)))}>View receipt</Button>
            <Button variant="outline" onClick={() => void downloadFundingFile(`/motor-funding/receipts/${popup.metadata.receipt_id}`, `${popup.metadata.receipt_no || 'receipt'}.pdf`).catch(error => toast.error(fundingError(error)))}>Download receipt</Button>
          </>}
          <Button onClick={() => closeDecision()}>OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </NotificationContext.Provider>;
}
